<template>
  <UiContainer class="help-view">
    <div class="help-view__header">
      <h1 class="help-view__title">Центр помощи</h1>
      <p class="help-view__lead">
        Ответы на частые вопросы, интерактивные туториалы и способы связи с нами.
      </p>
    </div>

    <!-- Быстрые действия -->
    <div class="help-view__actions">
      <UiCard class="help-view__action-card">
        <span class="help-view__action-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M2 4h7a2 2 0 0 1 2 2v14a2 2 0 0 0-2-2H2z" />
            <path d="M22 4h-7a2 2 0 0 0-2 2v14a2 2 0 0 1 2-2h7z" />
          </svg>
        </span>
        <h2 class="help-view__action-title">Интерактивные туториалы</h2>
        <div class="help-view__action-desc">Пошаговые инструкции по работе с системой</div>
        <UiButton variant="secondary" @click="showTutorials">Начать обучение</UiButton>
      </UiCard>

      <UiCard class="help-view__action-card">
        <span class="help-view__action-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"
            />
            <path
              d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"
            />
            <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
            <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
          </svg>
        </span>
        <h2 class="help-view__action-title">Онбординг</h2>
        <div class="help-view__action-desc">Знакомство с основными функциями</div>
        <UiButton variant="secondary" @click="startOnboarding">Начать знакомство</UiButton>
      </UiCard>

      <UiCard class="help-view__action-card">
        <span class="help-view__action-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
            />
          </svg>
        </span>
        <h2 class="help-view__action-title">Контакты</h2>
        <div class="help-view__action-desc">Как с нами связаться</div>
        <UiButton variant="secondary" to="/contacts">Перейти к контактам</UiButton>
      </UiCard>

      <UiCard class="help-view__action-card">
        <span class="help-view__action-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M14 18V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h1" />
            <path d="M14 9h4l3 3v4a1 1 0 0 1-1 1h-1" />
            <circle cx="7" cy="18" r="2" />
            <circle cx="17" cy="18" r="2" />
          </svg>
        </span>
        <h2 class="help-view__action-title">Доставка и оплата</h2>
        <div class="help-view__action-desc">Способы и условия</div>
        <UiButton variant="secondary" to="/delivery">Условия доставки</UiButton>
      </UiCard>
    </div>

    <!-- Частые вопросы -->
    <UiCard class="help-view__faq">
      <h2 class="help-view__faq-title">Частые вопросы</h2>
      <div class="help-view__accordion">
        <details class="help-view__details">
          <summary class="help-view__summary">Как оформить заказ?</summary>
          <div class="help-view__details-body">
            <p>
              Добавьте товары в корзину, перейдите к оформлению, заполните данные и подтвердите
              заказ.
            </p>
            <UiButton variant="secondary" @click="startTutorial('ordering')">
              Показать туториал
            </UiButton>
          </div>
        </details>
        <details class="help-view__details">
          <summary class="help-view__summary">Как стать поставщиком?</summary>
          <div class="help-view__details-body">
            <p>
              Подайте заявку на регистрацию поставщика. После модерации получите доступ к личному
              кабинету.
            </p>
            <UiButton variant="secondary" to="/suppliers/register">Стать поставщиком</UiButton>
          </div>
        </details>
        <details class="help-view__details">
          <summary class="help-view__summary">Как создать закупку?</summary>
          <div class="help-view__details-body">
            <p>
              Перейдите в раздел "Закупки", нажмите "Создать закупку" и заполните необходимые
              данные.
            </p>
            <UiButton variant="secondary" @click="startTutorial('procurement')">
              Туториал по закупкам
            </UiButton>
          </div>
        </details>
        <details class="help-view__details">
          <summary class="help-view__summary">Как добавить товар в избранное?</summary>
          <div class="help-view__details-body">
            <p>
              Нажмите на иконку сердечка на карточке товара. Это доступно без авторизации - список
              сохранится в браузере, а после входа в аккаунт перенесётся туда автоматически.
            </p>
            <UiButton variant="secondary" @click="startTutorial('favorites')">
              Туториал по избранному
            </UiButton>
          </div>
        </details>
      </div>
    </UiCard>

    <!-- Дополнительные ресурсы -->
    <div class="help-view__resources">
      <UiCard class="help-view__resource-card">
        <h2 class="help-view__resource-title">Документация</h2>
        <ul class="help-view__list">
          <li><RouterLink to="/privacy">Политика конфиденциальности</RouterLink></li>
          <li><RouterLink to="/terms">Условия использования</RouterLink></li>
        </ul>
      </UiCard>
      <UiCard class="help-view__resource-card">
        <h2 class="help-view__resource-title">Поддержка</h2>
        <p class="help-view__resource-lead">
          Если вы не нашли ответ на свой вопрос, свяжитесь с нами:
        </p>
        <ul class="help-view__contact-list">
          <li><strong>+7 (951) 852-06-24</strong> - Пн-Пт 9:00-18:00</li>
          <li><strong>support@labactive.ru</strong> - Ответим в течение 24 часов</li>
        </ul>
      </UiCard>
    </div>

    <UiModal v-model="showTutorialsDialog" title="" :max-width="800">
      <TutorialList @close="showTutorialsDialog = false" />
    </UiModal>
  </UiContainer>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { TutorialList, useTutorial } from '@/modules/tutorial/public'
import UiContainer from '@/shared/ui/UiContainer.vue'
import UiCard from '@/shared/ui/UiCard.vue'
import UiButton from '@/shared/ui/UiButton.vue'
import UiModal from '@/shared/ui/UiModal.vue'
import { RouterLink } from 'vue-router'

const { startTutorial, startOnboarding } = useTutorial()

const showTutorialsDialog = ref(false)

const showTutorials = () => {
  showTutorialsDialog.value = true
}
</script>

<style scoped>
.help-view {
  padding-top: 2rem;
  padding-bottom: 2rem;
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
}

.help-view__header {
  margin-bottom: 2rem;
}

.help-view__title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 1rem;
}

.help-view__lead {
  color: #586578;
  margin-bottom: 1.5rem;
}

.help-view__actions {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.help-view__action-card {
  padding: 1.25rem;
  height: 100%;
  min-height: 260px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  transition: transform 0.2s ease-in-out;
}

.help-view__action-card:hover {
  transform: translateY(-2px);
}

.help-view__action-icon {
  width: 56px;
  height: 56px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
  border-radius: 12px;
  background: #eff6ff;
  color: var(--color-accent);
}

.help-view__action-icon svg {
  width: 28px;
  height: 28px;
}

.help-view__action-title {
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  min-height: 2.5rem;
  display: flex;
  align-items: flex-start;
}

.help-view__action-desc {
  font-size: 0.875rem;
  color: #586578;
  margin-bottom: 1rem;
  min-height: 4.5rem;
}

.help-view__action-card :deep(.ui-button) {
  margin-top: auto;
}

.help-view__faq {
  padding: 1.25rem;
  margin-bottom: 2rem;
}

.help-view__faq-title {
  font-size: 1.125rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
}

.help-view__accordion {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.help-view__details {
  border-bottom: 1px solid #e2e8f0;
}

.help-view__details:last-child {
  border-bottom: none;
}

.help-view__summary {
  padding: 0.75rem 0;
  cursor: pointer;
  font-weight: 500;
  list-style: none;
}

.help-view__summary::-webkit-details-marker {
  display: none;
}

.help-view__details-body {
  padding-bottom: 1rem;
}

.help-view__details-body p {
  margin-bottom: 0.75rem;
}

.help-view__resources {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.help-view__resource-card {
  padding: 1.25rem;
  height: 100%;
}

.help-view__resource-title {
  font-size: 1.125rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
}

.help-view__resource-lead {
  font-size: 0.875rem;
  color: #586578;
  margin-bottom: 1rem;
}

.help-view__list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.help-view__list li {
  margin-bottom: 0.5rem;
}

.help-view__list a {
  color: #0f172a;
  text-decoration: none;
}

.help-view__list a:hover {
  text-decoration: underline;
}

.help-view__contact-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.help-view__contact-list li {
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

@media (max-width: 960px) {
  .help-view__action-card {
    min-height: 0;
  }

  .help-view__action-title,
  .help-view__action-desc {
    min-height: 0;
  }

  .help-view__resources {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
